import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { DegradedBanner } from '@/components/common/DegradedBanner'
import { BrandRow } from './BrandRow'
import { Footer } from './Footer'

type PageProps = {
  children: ReactNode
  /**
   * `lobby` widens the column and shortens the heading anchor. A workspace
   * list is unbounded in length, so the 21vh anchor a single-question form
   * wants would push most of the list below the fold.
   *
   * `center` drops the anchor and centres the card in the column — for
   * dead-end screens with no action, where a short card at 21vh reads as
   * stranded and there is no follow-on screen to stay aligned with.
   */
  variant?: 'default' | 'lobby' | 'center'
  /**
   * Rendered in the brand row, right-hand side. Only the authenticated
   * routes pass anything (the account menu); a signed-out or crashed page
   * has no identity to show.
   */
  identity?: ReactNode
}

/**
 * The page shell every route renders into: a two-pane split.
 *
 * The left pane owns everything the user acts on — brand row, content
 * column, legal foot. The right pane is decoration only (no content, no
 * controls) and is the first thing dropped below 900px.
 *
 * The heading inside `.formmid` is anchored to the viewport rather than
 * centred on its own content, so it lands on the same pixel row on every
 * screen — moving between the Lobby, an error and the signed-out card
 * doesn't make the page jump.
 */
export function Page({ children, variant = 'default', identity }: PageProps) {
  const lobby = variant === 'lobby'
  const centered = variant === 'center'

  return (
    <div className="app">
      <DegradedBanner />
      <div className="stage">
        <section className="pane pane--form">
          <BrandRow identity={identity} />
          <div
            className={cn(
              'formmid',
              lobby && 'formmid--lobby',
              centered && 'formmid--center',
            )}
          >
            <main className={cn('card', lobby && 'card--lobby')}>{children}</main>
          </div>
          <Footer />
        </section>

        <section className="pane pane--art" aria-hidden="true">
          <div className="art">
            <div className="art__slot">
              <div className="art__blob" />
              <img
                className="art__scene"
                src="/passport/workspace-scene.png"
                alt=""
                loading="lazy"
              />
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}
